import { generateToDocFn } from '@dadohr/shared/domainTypes/document';
import {
  IAdminNotificationPreference,
  IIssueDigestPreference,
  notificationPreferenceDocId
} from '@dadohr/shared/domainTypes/adminNotificationPreferences';
import { FS } from '@dadohr/shared/versions';
import { getCurrentUser } from '../currentUser';
import { setInTransaction, store } from '../firebase';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../firecache/documentListener';
import { now } from '../time';

const normalizeFn = (t: IAdminNotificationPreference) => t;
export const toAdminNotificationPreferenceDoc =
  generateToDocFn<IAdminNotificationPreference>(normalizeFn);

const collection = () => store().collection(FS.adminNotificationPreferences);

const preferenceStore = createDocumentListenerGetter<IAdminNotificationPreference>(
  (id) => collection().doc(id),
  toAdminNotificationPreferenceDoc
);

/**
 * Live listener for the current admin's issue-digest preference in a given org.
 * Returns `[Doc<IAdminNotificationPreference> | undefined, loading, error]`.
 * `undefined` data means the admin has never saved a preference (i.e. off).
 */
export const useIssueDigestPreference = (adminId: string, orgId: string) =>
  useDocumentListener(preferenceStore(notificationPreferenceDocId(adminId, orgId)));

/**
 * Create or update the current admin's issue-digest preference for their current
 * org. Preserves `createdAt` on updates.
 */
export const saveIssueDigestPreference = async (
  issueDigest: IIssueDigestPreference
): Promise<void> => {
  const currentUser = getCurrentUser();
  const timestamp = now();
  const adminId = currentUser.id;
  const orgId = currentUser.org.id;
  const docId = notificationPreferenceDocId(adminId, orgId);

  await setInTransaction<IAdminNotificationPreference>(
    collection().doc(docId),
    async (existing) => ({
      id: docId,
      adminId,
      orgId,
      issueDigest,
      createdAt: existing?.createdAt ?? timestamp,
      updatedAt: timestamp,
      updatedBy: adminId
    }),
    normalizeFn
  );
};

/**
 * Turn the digest off in one click while preserving the admin's last selection
 * (so re-enabling is easy). Backs the "Stop all notifications" button.
 */
export const stopAllIssueNotifications = async (
  currentIssueDigest: IIssueDigestPreference
): Promise<void> => {
  await saveIssueDigestPreference({ ...currentIssueDigest, enabled: false });
};
